import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Chip,
  Grid,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import regionsData from "@/app/features/company/data/regions_lite.json";
import citiesData from "@/app/features/company/data/cities_lite.json";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { createTheme, ThemeProvider } from "@mui/material/styles";

import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { routes } from "@/consts";
import useRegisterCompany from "@/hooks/use-register-company";
import useOffer from "@/hooks/useTender";

const primaryColor = "#644980";
const theme = createTheme({
  direction: "rtl", // Enable RTL
});
interface FormData {
    id: number | null;
    nameAr: string;
    nameEn: string;
    website: string;
    address: string;
    region: string;
    city: string;
    registrationNumber: string;
    ownerType: string;
    stampedAuthorizationForm: File | null;
    plaseOfIssue: string;
    turnover: number;
    type: string;
    activities: string;
    categoriesIds: string[];
    contactInfo: string;
    creationDate: string;
    expireDate: string;
    CompanyNr: string;
}

/*interface CategoryOption {
    value: any; // Use specific types here, like string or number, if known
    label: any; // Use string if label is always a string
}*/

interface Category {
  id: string;
  name: string;
  parentId: string | null;
}
const CreateCompanyPage = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const navigate = useNavigate(); // Navigation hook
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const [activeStep, setActiveStep] = useState(0);
  const [regions, setRegions] = useState<{ value: string; label: string }[]>(
    []
  );
  const [cities, setCities] = useState<{ value: string; label: string }[]>([]);
  const [mockCategories, setMockCategories] = useState<Category[]>([]);
  const [displayedCategories, setDisplayedCategories] = useState<Category[]>(
    []
  );
  const { getcategories } = useOffer();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const { registerCompany } = useRegisterCompany(); // Use the custom hook
  const [formData, setFormData] = useState<FormData>({
    id: null,
    nameAr: "",
    nameEn: "",
    website: "",
    address: "",
    region: "",
    city: "",
    registrationNumber: "",
    ownerType: "Owner", // Default value
    stampedAuthorizationForm: null,
    plaseOfIssue: "",
    turnover: 0,
    type: "",
    activities: "",
    categoriesIds: [],
    contactInfo: authUser?.email || '',
    creationDate: '',
    expireDate: '',
    CompanyNr: '',
  });
  useEffect(() => {
    if (myCompany) {
      setFormData((prevData) => ({
        ...prevData,
        id: myCompany.id,
        nameAr: myCompany.nameAr,
        nameEn: myCompany.nameEn,
        website: myCompany.website,
        address: myCompany.address,
        region: myCompany.region,
        city: myCompany.city,
        registrationNumber: myCompany.registrationNumber,
        ownerType: myCompany.ownerType,
        stampedAuthorizationForm: myCompany.stampedAuthorizationForm,
        plaseOfIssue: myCompany.plaseOfIssue,
        turnover: myCompany.turnover,
        type: myCompany.type,
        activities: myCompany.activities,
        categoriesIds: myCompany.categoriesIds,
        contactInfo: myCompany.contactInfo,
        creationDate: myCompany.creationDate,
        expireDate: myCompany.expireDate,
      }));
    }
  }, [myCompany]);
  //const steps = ['{t("company.info")}', 'اختيار الفئات'];

  //const handleNext = () => setActiveStep((prevStep) => prevStep + 1);
  //const handleBack = () => setActiveStep((prevStep) => prevStep - 1);
  // Snackbar (Toastr) State
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prevData: FormData) => ({ ...prevData, [name]: value }));
  };

  const handleCategoryToggle = (categoryId: string) => {
    if (selectedCategories.includes(categoryId)) {
      // Remove the category and all its subcategories from selected and displayed
      const removeSubcategories = (parentId: string): string[] => {
        const children = mockCategories.filter(
          (category) => category.parentId === parentId
        );
        let subcategories = children.map((category) => category.id);
        children.forEach((child) => {
          subcategories = [...subcategories, ...removeSubcategories(child.id)];
        });
        return subcategories;
      };

      const categoriesToUncheck = [
        categoryId,
        ...removeSubcategories(categoryId),
      ];
      const categoriesToRemove = removeSubcategories(categoryId);

      setSelectedCategories((prevSelected) =>
        prevSelected.filter((id) => !categoriesToUncheck.includes(id))
      );
      setDisplayedCategories((prevDisplayed) =>
        prevDisplayed.filter(
          (category) => !categoriesToRemove.includes(category.id)
        )
      );
    } else {
      // Add the category to selected and show its subcategories
      setSelectedCategories((prevSelected) => [...prevSelected, categoryId]);

      const childCategories = mockCategories.filter(
        (category) => category.parentId === categoryId
      );
      setDisplayedCategories((prevDisplayed) =>
        [...prevDisplayed, ...childCategories].filter(
          (category, index, self) =>
            self.findIndex((c) => c.id === category.id) === index // Remove duplicates
        )
      );
    }
  };

  const handleSubmit = async () => {
    const updatedFormData = new FormData();
    updatedFormData.append("id", formData.id?.toString() || "");
    updatedFormData.append("nameAr", formData.nameAr);
    updatedFormData.append("nameEn", formData.nameEn);
    updatedFormData.append("website", formData.website);
    updatedFormData.append("address", formData.address);
    updatedFormData.append("region", formData.region);
    updatedFormData.append("city", formData.city);
    updatedFormData.append("registrationNumber", formData.registrationNumber);
    updatedFormData.append("ownerType", formData.ownerType);
    if (formData.stampedAuthorizationForm) {
      updatedFormData.append(
        "stampedAuthorizationForm",
        formData.stampedAuthorizationForm
      );
    }
    updatedFormData.append("plaseOfIssue", formData.plaseOfIssue);
    updatedFormData.append("turnover", formData.turnover.toString());
    updatedFormData.append("type", formData.type);
    updatedFormData.append("activities", formData.activities);
    updatedFormData.append("categoriesIds", JSON.stringify(selectedCategories));
    updatedFormData.append("contactInfo", formData.contactInfo);
    updatedFormData.append("creationDate", formData.creationDate);
    updatedFormData.append("expireDate", formData.expireDate);

    try {
      await registerCompany(updatedFormData);
      setSnackbarMessage(t("alerts.success.companyRegistered"));
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setTimeout(() => navigate(routes.home), 2000);
    } catch (error) {
      setSnackbarMessage(t("alerts.error.ErrorRegisterCompany"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
    //navigate('/'); // Navigate to another page after creation
  };

  useEffect(() => {
    setRegions(
      regionsData.map((region) => ({
        value: region.region_id.toString(),
        label: region.name_ar,
      }))
    );
  }, []);
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const Categories = await getcategories();
        if (Categories) {
          const uniqueCategories: Category[] = Categories.data.filter(
            (item: Category, index: number, self: Category[]) =>
              index === self.findIndex((t) => t.id === item.id) // Only keep unique ids
          );
          setMockCategories(uniqueCategories);

          // Set initial categories to those with parentId === null
          setDisplayedCategories(
            uniqueCategories.filter(
              (category: Category) => category.parentId === null
            )
          );
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const selectedRegion = formData.region;
    if (selectedRegion) {
      const filteredCities = citiesData
        .filter((city) => city.region_id === parseInt(selectedRegion))
        .map((city) => ({
          value: city.city_id.toString(),
          label: city.name_ar,
        }));
      setCities(filteredCities);
    } else {
      setCities([]);
    }
  }, [formData.region]);

  return (
    <ThemeProvider theme={theme}>
      <div
        className="flex flex-col min-h-screen"
        style={{ background: "#f8fafa !important" }}
      >
        <div
          className="xs:flex xs:flex-col items-center "
          style={{ paddingTop: 130 }}
        >
          <main className="flex min-h-screen items-center w-full sm:w-3/5 flex-col">
            <section className="w-full max-w-xs bg-primary-white p-5 rounded-md xs:max-w-5xl py-6">
              <div className="flex flex-col">
                <h1 className="sm:text-2xl text-base font-semibold">
                  {t("checkout.fields.company")}
                </h1>
                {activeStep === 0 && (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ textAlign: "right" }}
                      >
                        {t("company.fields.name")}
                      </Typography>
                      <TextField
                        name="nameAr"
                        value={formData.nameAr}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ textAlign: "right" }}
                      >
                        {t("company.fields.nameEn")}
                      </Typography>
                      <TextField
                        name="nameEn"
                        value={formData.nameEn}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                      />
                    </Box>
                   
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ textAlign: "right" }}
                      >
                        {t("company.fields.commercialRegistration")}
                      </Typography>
                      <TextField
                        name="registrationNumber"
                        value={formData.registrationNumber}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ textAlign: "right" }}
                      >
                        {t("company.fields.address")}
                      </Typography>
                      <TextField
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ textAlign: "right" }}
                      >
                        {t("company.placeholders.selectRegion")}
                      </Typography>
                      <TextField
                        name="region"
                        value={formData.region}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                        select
                      >
                        <MenuItem key={0} value="">
                          {t("company.placeholders.selectRegion")}
                        </MenuItem>
                        {regions.map((region) => (
                          <MenuItem key={region.value} value={region.value}>
                            {region.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ textAlign: "right" }}
                      >
                        {t("company.placeholders.selectCity")}
                      </Typography>
                      <TextField
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                        select
                      >
                        <MenuItem key={0} value="">
                          {t("company.placeholders.selectCity")}
                        </MenuItem>
                        {cities.map((city) => (
                          <MenuItem key={city.value} value={city.value}>
                            {city.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  </Box>
                )}
                {activeStep === 1 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {t("categories.select")}
                    </Typography>
                    <Grid container spacing={2}>
                      {displayedCategories.map((category) => (
                        <Grid item key={category.id}>
                          <Chip
                            sx={{
                              backgroundColor: selectedCategories.includes(
                                category.id
                              )
                                ? primaryColor
                                : "#f5f5f5",
                              color: selectedCategories.includes(category.id)
                                ? "#fff"
                                : "#333",
                              "&:hover": {
                                backgroundColor: primaryColor,
                                color: "#fff",
                              },
                            }}
                            label={category.name}
                            color={
                              selectedCategories.includes(category.id)
                                ? "primary"
                                : "default"
                            }
                            onClick={() => handleCategoryToggle(category.id)}
                            clickable
                            size="small"
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 4,
                  }}
                >
                  <Button
                    disabled={activeStep === 0}
                    onClick={() => setActiveStep(activeStep - 1)}
                    variant="outlined"
                    sx={{ borderColor: primaryColor, color: primaryColor }}
                  >
                    {t("actions.previousStep")}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    sx={{
                      backgroundColor: primaryColor,
                      "&:hover": { backgroundColor: "#503a6b" },
                    }}
                  >
                    {t("actions.submit")}
                  </Button>
                </Box>
              </div>
            </section>
          </main>
        </div>
        {/* Material UI Snackbar for Toastr Notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    </ThemeProvider>
  );
};

export default CreateCompanyPage;
